import dayjs from "dayjs";
import { COUNTRY_CODE_OPTIONS } from "../../common/country-code-select";

export const PAGE_SIZES = [25, 50, 100];
export const DEFAULT_ISO = "IN";
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const UPLOAD_EXTENSIONS = ["csv", "xlsx", "xls"];
export const SALUTATIONS = ["Mr.", "Ms.", "Mrs.", "Dr.", "Prof.", "M/S"];

export const TABS = [
  { value: "all", label: "All" },
  { value: "annual", label: "Annual" },
  { value: "life", label: "Life" },
  { value: "corporate_associate", label: "Corporate & associate" },
  { value: "needs_attention", label: "Needs attention" },
  { value: "inactive", label: "Inactive" },
];

// Table column id -> the API's `ordering` key (spec: sortable by Name, Member since, Valid till).
export const SORT_KEYS = { member: "name", since: "member_since", status: "valid_till" };

export const DEFAULT_FILTERS = {
  q: "",
  tab: "all",
  classIds: [],
  city: "",
  state: "",
  country: "",
  ordering: "",
  page: 0, // zero-based in the UI, one-based in the API and the URL
  pageSize: PAGE_SIZES[0],
};

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------
export const formatDate = (iso) => (iso ? dayjs(iso).format("DD MMM YYYY") : "—");
export const formatDateTime = (iso) => (iso ? dayjs(iso).format("DD MMM YYYY, hh:mm A") : "—");

export const memberStatus = (member) => {
  if (member.status === "inactive") return { label: "Inactive", color: "default" };
  if (member.is_life) return { label: "Life member", color: "info" };
  return { label: `Active · till ${formatDate(member.valid_till)}`, color: "success" };
};

export const syncStatusChip = (status) =>
  ({
    synced: { label: "Synced", color: "success" },
    failed: { label: "Failed", color: "error" },
  })[status] || { label: "Pending", color: "default" };

export const initialsOf = (name) => {
  const parts = String(name || "")
    .replace(/[^A-Za-z ]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

export const issueLabel = (count) => (count ? `${count} ${count === 1 ? "issue" : "issues"}` : "Complete");

// ---------------------------------------------------------------------------
// Country code <-> ISO (the select works in ISO codes, the API in calling codes)
// ---------------------------------------------------------------------------
export const callingCodeForIso = (iso) =>
  COUNTRY_CODE_OPTIONS.find((option) => option.iso === iso)?.callingCode || "91";

export const isoForCallingCode = (code) =>
  COUNTRY_CODE_OPTIONS.find((option) => String(option.callingCode) === String(code))?.iso || DEFAULT_ISO;

// ---------------------------------------------------------------------------
// Filters <-> API params <-> URL query string
// ---------------------------------------------------------------------------
export const filtersToParams = (filters) => ({
  q: filters.q,
  tab: filters.tab === "all" ? "" : filters.tab,
  class: filters.classIds,
  city: filters.city,
  state: filters.state,
  country: filters.country,
  ordering: filters.ordering,
  page: filters.page + 1,
  page_size: filters.pageSize,
});

/** What the summary endpoint needs: search + filters, but not the tab, order or page. */
export const filtersToSummaryParams = (filters) => ({
  q: filters.q,
  class: filters.classIds,
  city: filters.city,
  state: filters.state,
  country: filters.country,
});

export const filtersToSearch = (filters) => {
  const search = new URLSearchParams();
  const params = filtersToParams(filters);
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach((item) => search.append(key, item));
    else if (value !== "" && value !== undefined && value !== null) search.append(key, value);
  });
  if (filters.page === 0) search.delete("page");
  if (filters.pageSize === PAGE_SIZES[0]) search.delete("page_size");
  const text = search.toString();
  return text ? `?${text}` : "";
};

export const filtersFromSearch = (searchString) => {
  const search = new URLSearchParams(searchString || "");
  const pageSize = Number(search.get("page_size"));
  const page = Number(search.get("page"));
  const tab = search.get("tab");
  return {
    ...DEFAULT_FILTERS,
    q: search.get("q") || "",
    tab: TABS.some((t) => t.value === tab) ? tab : "all",
    classIds: search.getAll("class").filter((v) => /^\d+$/.test(v)),
    city: search.get("city") || "",
    state: search.get("state") || "",
    country: search.get("country") || "",
    ordering: Object.values(SORT_KEYS).some((key) => [key, `-${key}`].includes(search.get("ordering")))
      ? search.get("ordering")
      : "",
    page: page > 1 ? page - 1 : 0,
    pageSize: PAGE_SIZES.includes(pageSize) ? pageSize : DEFAULT_FILTERS.pageSize,
  };
};

/** Anything other than the tab, order and paging counts as a filter ("Clear filters"). */
export const hasActiveFilters = (filters) =>
  Boolean(
    filters.q || filters.classIds.length || filters.city || filters.state || filters.country ||
      filters.tab !== "all",
  );

// ---------------------------------------------------------------------------
// Member form
// ---------------------------------------------------------------------------
export const FIELD_LABELS = {
  membership_id: "Membership ID",
  salutation: "Salutation",
  full_name: "Name",
  membership_class: "Class",
  country_code: "Country code",
  mobile: "Mobile",
  office_phone: "Office phone",
  residence_phone: "Residence phone",
  email: "Email",
  dob: "Date of birth",
  anniversary: "Anniversary",
  address_type: "Address type",
  firm_name: "Firm name",
  address_line1: "Address line 1",
  address_line2: "Address line 2",
  area: "Area",
  district: "District",
  city: "City",
  state: "State",
  country: "Country",
  pin_code: "Pin code",
  registration_date: "Member since",
  membership_end_date: "Valid till",
  services_to_date: "Services till",
  icai_no: "ICAI no.",
  icai_date: "ICAI date",
  gstin: "GSTIN",
  linked_member: "Linked member",
  phone: "Mobile",
  membership_class_id: "Class",
  linked_member_id: "Linked member",
};

export const EMPTY_MEMBER_VALUES = {
  membership_id: "",
  salutation: "",
  full_name: "",
  membership_class: "",
  country_iso: DEFAULT_ISO,
  mobile: "",
  office_phone: "",
  residence_phone: "",
  email: "",
  dob: "",
  anniversary: "",
  address_type: "",
  firm_name: "",
  address_line1: "",
  address_line2: "",
  area: "",
  district: "",
  city: "",
  state: "",
  country: "",
  pin_code: "",
  registration_date: "",
  membership_end_date: "",
  services_to_date: "",
  icai_no: "",
  icai_date: "",
  gstin: "",
};

const DATE_FIELDS = ["dob", "anniversary", "registration_date", "membership_end_date", "services_to_date", "icai_date"];

/** API member (detail) -> form values. */
export const memberToFormValues = (member) => {
  const values = { ...EMPTY_MEMBER_VALUES };
  Object.keys(EMPTY_MEMBER_VALUES).forEach((key) => {
    if (member[key] !== undefined && member[key] !== null) values[key] = member[key];
  });
  values.membership_class = member.membership_class ? String(member.membership_class) : "";
  values.mobile = member.phone || "";
  values.country_iso = isoForCallingCode(member.country_code);
  return values;
};

const FIELDS_SENT_AS_IS = Object.keys(EMPTY_MEMBER_VALUES).filter(
  (key) => !["membership_id", "membership_class", "country_iso", "mobile", ...DATE_FIELDS].includes(key),
);

/**
 * Form values -> request body. For an edit pass `initial` so only what changed is sent
 * (the server then re-checks the phone and duplicates only if the number changed).
 */
export const toPayload = (values, initial = null) => {
  const payload = {};
  const changed = (key) => !initial || values[key] !== initial[key];

  if (!initial && values.membership_id.trim()) payload.membership_id = values.membership_id.trim();
  FIELDS_SENT_AS_IS.forEach((key) => {
    if (changed(key) && (initial || values[key] !== "")) payload[key] = values[key];
  });
  DATE_FIELDS.forEach((key) => {
    if (changed(key) && (initial || values[key] !== "")) payload[key] = values[key] || null;
  });
  if (changed("membership_class") && (initial || values.membership_class !== "")) {
    payload.membership_class = values.membership_class ? Number(values.membership_class) : null;
  }
  if (!initial || changed("mobile") || changed("country_iso")) {
    payload.mobile = values.mobile.trim();
    payload.country_code = callingCodeForIso(values.country_iso);
  }
  return payload;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MEMBERSHIP_ID_PATTERN = /^[A-Za-z0-9/_-]{1,50}$/;
const MAX_AGE_YEARS = 120;

/** Same rules and messages as the server (spec 2.5); the server stays the source of truth. */
export const validateMember = (values, { mode = "add" } = {}) => {
  const errors = {};
  const mobile = values.mobile.replace(/[\s\-().]/g, "");
  const code = callingCodeForIso(values.country_iso);

  if (!mobile) {
    errors.mobile = code === "91" ? "Enter a valid 10-digit mobile number." : "Enter a valid mobile number.";
  } else if (code === "91" && !/^(\+|00)/.test(mobile) && !/^[6-9]\d{9}$/.test(mobile.replace(/^0/, ""))) {
    errors.mobile = "Enter a valid 10-digit mobile number.";
  }
  if (values.email && !EMAIL_PATTERN.test(values.email.trim())) errors.email = "Enter a valid email address.";
  if (values.dob) {
    const dob = dayjs(values.dob);
    if (!dob.isValid() || dob.isAfter(dayjs(), "day") || dob.isBefore(dayjs().subtract(MAX_AGE_YEARS, "year"))) {
      errors.dob = "Enter a valid date of birth.";
    }
  }
  if (values.registration_date && values.membership_end_date &&
      dayjs(values.membership_end_date).isBefore(dayjs(values.registration_date), "day")) {
    errors.membership_end_date = "Valid till can't be before Member since.";
  }
  if (values.pin_code && ["india", "in", "bharat"].includes(values.country.trim().toLowerCase()) &&
      !/^\d{6}$/.test(values.pin_code)) {
    errors.pin_code = "Pin code must be 6 digits.";
  }
  if (values.full_name.length > 150) errors.full_name = "Name is too long.";
  if (mode === "add" && values.membership_id.trim() && !MEMBERSHIP_ID_PATTERN.test(values.membership_id.trim())) {
    errors.membership_id = "Membership ID can have up to 50 letters, digits, - / _";
  }
  return errors;
};

// ---------------------------------------------------------------------------
// Activity feed
// ---------------------------------------------------------------------------
export const describeActivity = (activity) => {
  const changes = activity.changes || {};
  switch (activity.action) {
    case "created":
      return "Member added";
    case "updated": {
      const fields = Object.keys(changes).map((key) => FIELD_LABELS[key] || key);
      return fields.length ? `Details edited: ${fields.join(", ")}` : "Details edited";
    }
    case "imported":
      return changes.result === "created" ? "Created by file import" : "Updated by file import";
    case "synced":
      return "Synced to Broadcast Audience";
    case "sync_failed":
      return `Audience sync failed${changes.error ? `: ${changes.error}` : ""}`;
    case "deactivated":
      return "Marked inactive";
    case "reactivated":
      return "Reactivated";
    case "deleted":
      return "Deleted";
    default:
      return activity.action;
  }
};
